import React, { createContext, useContext } from 'react';
import { useRouter } from 'next/router';

import useAuth from '@lib/hooks/useAuth';
import useFeature from '@lib/hooks/useFeature';
import useLastOrderDefaults from '@lib/hooks/useLastOrderDefaults';
import useSheetOpenRequest from '@lib/hooks/useSheetOpenRequest';

import DefaultLocationModal from './DefaultLocationModal';
import useActiveStore from '@lib/hooks/useActiveStore';
import { skipLocation } from '@lib/analytics/google';
import { push } from '@lib/analytics';
import { trackStackAdaptOrderNow } from '@lib/analytics/stackadapt';

interface DefaultLocationModalContextType {
    openModal: () => void;
    forceShow: () => void;
    closeModal: () => void;
    isOpen: boolean;
    /** 0 while closed; a new value is a new request to show the modal. See useSheetOpenRequest. */
    openRequestId: number;
}

const DefaultLocationModalContext = createContext<DefaultLocationModalContextType | undefined>(
    undefined
);

export const useDefaultLocationModal = () => {
    const context = useContext(DefaultLocationModalContext);
    if (!context) {
        throw new Error('useDefaultLocationModal must be used within DefaultLocationModalProvider');
    }
    return context;
};

interface DefaultLocationModalProviderProps {
    children: React.ReactNode;
}

export const DefaultLocationModalProvider: React.FC<DefaultLocationModalProviderProps> = ({
    children
}) => {
    // Kept identical to the native provider. The web modal is declarative (`visible={open}`) and
    // cannot wedge the way the native bottom sheet did (JSAD-7298) — this is for one context shape.
    const { openRequestId, isOpen, open: showModal, close: closeModal } = useSheetOpenRequest();
    const router = useRouter();
    const { user, defaultLocation } = useAuth();
    const isDefaultLocationEnabled = useFeature('default-location', false);
    const { orderNow } = useLastOrderDefaults();
    const activeStore = useActiveStore();

    const openModal = () => {
        // Every web "Order" / "Start Order" CTA funnels through here — Optimal's
        // "Order Now" pixel (JSAD-7304) fires on the click, whichever branch follows.
        // The one CTA that bypasses openModal(), reorder onto a non-empty cart, fires
        // it itself in useReorder.
        trackStackAdaptOrderNow();

        if (activeStore) {
            router.push(`/menu/${activeStore.id}`);
            return;
        }

        const currentPath = router.pathname;
        const blockedPaths = ['/menu', '/cart', '/checkout', '/location', '/order-location'];

        if (blockedPaths.some((path) => currentPath.includes(path)) || !isDefaultLocationEnabled) {
            orderNow();
            return;
        }

        // A past delivery order whose address has since been deleted leaves the
        // profile's `defaultLocation` without a `deliveryAddress`. There's no
        // address to start the order from, so treat it as "no default location"
        // and fall through to `orderNow()`, which routes to the locations screen.
        const hasUsableDefaultLocation =
            !!defaultLocation &&
            (defaultLocation.orderType === 'pickup' || !!defaultLocation.deliveryAddress);

        if (user && hasUsableDefaultLocation) {
            showModal();
            push(skipLocation());
            return;
        }

        orderNow();
    };

    const forceShow = () => showModal();

    const contextValue: DefaultLocationModalContextType = {
        openModal,
        forceShow,
        closeModal,
        isOpen,
        openRequestId
    };

    return (
        <DefaultLocationModalContext.Provider value={contextValue}>
            {children}
            <DefaultLocationModal
                open={isOpen}
                defaultLocation={defaultLocation}
                onClose={closeModal}
            />
        </DefaultLocationModalContext.Provider>
    );
};
